import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { IoMdDownload } from "react-icons/io";
import { useParams } from 'react-router-dom';
import { ActionIcon, Tooltip } from 'rizzui';
import DeletePopover from '../../../../../components/buttons/DeletePopover';
import { Loader } from '../../../../../components/loader/SpinnerLogo';
import { FilterDrawerView } from '../../../../../components/table/components/TableFilter';
import CustomTable from '../../../../../components/table/CustomTable';
import useHandleDownloadDocument from '../../../../../hooks/useDownloadDocument';
import useFetch from '../../../../../hooks/useFetch';
import useFilters from '../../../../../hooks/useFilters';
import { DocumentsService } from '../../../../../services/document/documentsService';
import { UserService } from '../../../../../services/user/userService';
import DocumentsFilters from './DocumentsFilters';

interface UserDocumentsProps { }

const columnOrder = [
    'id',
    'originalName',
    'documentType',
    'user',
    'extension',
    'createdAt',
];

const UserDocuments: React.FC<UserDocumentsProps> = ({ }) => {

    // STATES

    const [updateList, setUpdateList] = useState<boolean>(false);
    const [openFilters, setOpenFilters] = useState<boolean>(false);

    // HOOKS

    const { id = '' } = useParams<{ id: string }>();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFilters();
    const { handleDownloadDocumentNewTab } = useHandleDownloadDocument();
    const docService = new DocumentsService();
    const service = new UserService();

    // DATA FETCHING

    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        if (!filters.filter_filters?.user) return null;
        const response = await service.listUserDocuments(filters);
        return response.getResponseData();
    }, [filters]));

    // FUNCTIONS

    // --------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJA EL BORRADO MULTIPLE DE DOCUMENTOS
     * @EN HANDLES THE MULTIPLE DELETION OF DOCUMENTS
     * 
     * @param ids 
     */
    // --------------------------------------------------------------------------------------------------------------------------
    const handleMultiDelete = async (ids: string[]) => {
        await docService.multiDeleteDocuments(ids);
        setUpdateList(true);
        refetch();
    };
    // --------------------------------------------------------------------------------------------------------------------------

    // --------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJA EL ORDEN DE LAS COLUMNAS
     * @EN HANDLES THE ORDER OF THE COLUMNS
     * 
     * @param data
     * @param columnOrder
     */
    // --------------------------------------------------------------------------------------------------------------------------
    const orderedData = data?.data.map((row: any) => {
        const formattedRow: any = {
            id: row?.document?.id,
            originalName: row?.document?.originalName,
            documentType: row?.documentType?.name,
            user: row.user.name + (row?.user?.lastName ? ` ${row?.user?.lastName}` : ''),
            extension: row?.document?.extension,
            createdAt: moment(row?.createdAt?.date).format('DD/MM/YYYY'),
        };

        const orderedRow: any = {};
        columnOrder.forEach((column) => {
            orderedRow[column] = formattedRow[column];
        });

        return orderedRow;
    });
    // --------------------------------------------------------------------------------------------------------------------------

    // USE EFFECTS

    useEffect(() => {
        updateFilters({ user: id });
    }, [id]);

    useEffect(() => {
        if (updateList) {
            refetch();
            setUpdateList(false);
        }
    }, [updateList]);

    // RENDER

    return (
        <>
            <div className="container mx-auto p-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="md:col-span-3 col-span-1">
                        <div className="pt-7 mb-10 grid gap-7 divide-y-2 divide-dashed divide-gray-200">
                            {loading
                                ? <Loader />
                                : data?.data.length > 0 && (
                                    <CustomTable
                                        id={'users-documents-table'}
                                        columnOrder={columnOrder}
                                        data={orderedData}
                                        columnsNotShown={['extension', 'fileName', 'status']}
                                        actions={[
                                            {
                                                label: 'Descargar',
                                                render: (row: any) => {
                                                    return (
                                                        <Tooltip
                                                            size="sm"
                                                            content={'Descargar documento'}
                                                            placement="top"
                                                            color="invert"
                                                        >
                                                            <ActionIcon
                                                                as="span"
                                                                size="sm"
                                                                variant="outline"
                                                                className="hover:!border-gray-900 hover:text-gray-700 cursor-pointer bg-white"
                                                                onClick={() => handleDownloadDocumentNewTab(docService.downloadDocument, row.id, row.originalName, row.extension)}
                                                            >
                                                                <IoMdDownload className="h-4 w-4" size={20} />
                                                            </ActionIcon>
                                                        </Tooltip>
                                                    );
                                                },
                                            },
                                            {
                                                label: 'Delete',
                                                render: (row: any) => {
                                                    return (
                                                        <DeletePopover
                                                            title={`Eliminar documento`}
                                                            description={`¿Estás seguro de que deseas eliminar ${row.originalName}?`}
                                                            onDelete={() => handleMultiDelete([row.id])}
                                                        />
                                                    );
                                                },
                                            },
                                        ]}
                                        handleMultipleDelete={handleMultiDelete}
                                        filters={filters}
                                        updateFilters={updateFilters}
                                        updateFilterOrder={updateFilterOrder}
                                        defaultOrder={filters.filter_order || undefined}
                                        paginationData={{
                                            pageSize: filters.limit,
                                            currentPage: filters.page,
                                            pageCount: data ? data.lastPage : 1,
                                            totalCount: data?.totalRegisters,
                                            handlePagination: updatePage,
                                            handlePerPage: updatePageSize,
                                        }}
                                        toggleFilters={() => setOpenFilters(!openFilters)}
                                    />
                                )
                            }
                        </div>
                    </div>
                </div>
            </div>

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros Documentos'} onClear={() => resetFilters(50)}>
                <div className="grid grid-cols-1 gap-6">
                    <DocumentsFilters filters={filters} updateFilters={updateFilters} />
                </div>
            </FilterDrawerView>
        </>
    );
};

export default UserDocuments;